<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-form ref="form">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('routes.pod.menuEmbedMedia.title') }}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="scrollable mt-4">
          <div data-cy="podMenu-tools-embedMediaPreview-form">
            <h3 data-cy="podMenu-tools-embedMediaPreview-title">
              {{ $t('routes.pod.menuEmbedMedia.previewFile') }}
            </h3>

            <div class="embed-media-dialog--asset-props-list">
              <div class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.fileType') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ transcodedAsset.filetype }}
                </span>
              </div>

              <div class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.fileSize') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ formatSize(transcodedAsset.size || 0, 2) }}
                </span>
              </div>

              <div v-if="isVideoPod || isImagePod" class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.resolution') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ transcodedAsset.resolution }}
                </span>
              </div>

              <div v-if="isVideoPod || isImagePod" class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.runtime') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ videoDuration(parseFloat(transcodedAsset.duration || 0)) }}
                </span>
              </div>
            </div>

            <div v-if="itemsLoaded">
              <div v-if="previewLink" class="d-flex">
                <div class="flex-grow-1">
                  <v-textarea
                    v-bind:model-value="previewEmbedCode"
                    rows="4"
                    variant="solo-filled"
                    flat
                    hide-details
                    readonly
                    :label="$t('routes.pod.menuEmbedMedia.previewAssetEmbedCode')"
                    data-cy="podMenu-tools-embedMediaPreview-code"
                    class="mt-2"
                  />
                </div>
                <div class="flex-grow-0  d-flex flex-column justify-space-between pl-2">
                  <v-tooltip
                    location="bottom"
                    z-index="1200"
                    :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        type="button"
                        v-on:click="copyToClipboard('web')"
                        variant="flat"
                        rounded="10"
                        tile icon
                        data-cy="podMenu-tools-embedMediaPreview-copyUrl-btn"
                        :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                      >
                        <v-icon tile>mdi-clipboard-file-outline</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('routes.pod.menuEmbedMedia.copyToClipboard') }}
                  </v-tooltip>

                  <DeleteEmbedLinkDialog
                    v-if="$canControlPodService()"
                    v-model:embedlink="previewLink"
                  >
                    <template v-slot:activator="{ props: dialogActivatorProps }">
                      <v-tooltip
                        location="bottom"
                        z-index="1200"
                        :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="{ ...dialogActivatorProps, ...props }"
                            variant="flat"
                            rounded="10"
                            tile icon
                            data-cy="podMenu-tools-embedMediaOrigin-deleteLink-btn"
                            :aria-label="$t('common.delete')"
                          >
                            <v-icon color="primary">mdi-delete-outline</v-icon>
                          </v-btn>
                        </template>
                        {{ $t('common.delete') }}
                      </v-tooltip>
                    </template>
                  </DeleteEmbedLinkDialog>
                </div>
              </div>
              <div v-else>
                <v-btn
                  v-on:click.prevent="createLink('web')"
                  :disabled="isSubmitPreviewDisabled"
                  block
                  elevation=0
                  color="primary"
                  class="mt-2"
                  data-cy="podMenu-tools-embedMediaPreview-createLink-btn"
                >
                  {{ $t('routes.pod.menuEmbedMedia.createLink') }}
                </v-btn>
              </div>
            </div>
          </div>

          <hr class="header-hr my-4"/>

          <div data-cy="podMenu-tools-embedMediaOrigin-form">
            <h3 data-cy="podMenu-tools-embedMediaOrigin-title">
              {{ $t('routes.pod.menuEmbedMedia.originalFile') }}
            </h3>


            <div class="embed-media-dialog--asset-props-list">
              <div class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.fileType') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ rawAsset.filetype }}
                </span>
              </div>

              <div class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.fileSize') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ formatSize(rawAsset.size || 0, 2) }}
                </span>
              </div>

              <div v-if="isVideoPod || isImagePod" class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.resolution') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ rawAsset.resolution }}
                </span>
              </div>

              <div v-if="isVideoPod || isImagePod" class="embed-media-dialog--asset-props-item">
                <span class="embed-media-dialog--asset-props-item-name">
                  {{ $t('routes.pod.menuInfo.runtime') }}
                </span>
                <span class="embed-media-dialog--asset-props-item-value">
                  {{ videoDuration(parseFloat(rawAsset.duration || 0)) }}
                </span>
              </div>
            </div>

            <div v-if="itemsLoaded">
              <div v-if="originLink" class="d-flex">
                <div class="flex-grow-1">
                  <v-textarea
                    v-bind:model-value="originEmbedCode"
                    class="mt-2"
                    rows="4"
                    variant="solo-filled"
                    :label="$t('routes.pod.menuEmbedMedia.originalAssetEmbedCode')"
                    flat
                    hide-details
                    readonly
                    data-cy="podMenu-tools-embedMediaOrigin-code"
                 />
                </div>

                <div class="flex-grow-0 d-flex flex-column justify-space-between pl-2">
                  <v-tooltip
                    location="bottom"
                    z-index="1200"
                    :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        type="button"
                        @click="copyToClipboard('origin')"
                        variant="flat"
                        rounded="10"
                        tile icon
                        data-cy="podMenu-tools-embedMediaOrigin-copyUrl-btn"
                        :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                      >
                        <v-icon tile>mdi-clipboard-file-outline</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('routes.pod.menuEmbedMedia.copyToClipboard') }}
                  </v-tooltip>

                  <DeleteEmbedLinkDialog
                    v-if="$canControlPodService()"
                    v-model:embedlink="originLink"
                  >
                    <template v-slot:activator="{ props: dialogActivatorProps }">
                      <v-tooltip
                        location="bottom"
                        z-index="1200"
                        :aria-label="$t('routes.pod.menuEmbedMedia.copyToClipboard')"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="{ ...dialogActivatorProps, ...props }"
                            variant="flat"
                            rounded="10"
                            tile icon
                            data-cy="podMenu-tools-embedMediaOrigin-deleteLink-btn"
                            :aria-label="$t('common.delete')"
                          >
                            <v-icon color="primary">mdi-delete-outline</v-icon>
                          </v-btn>
                        </template>
                        {{ $t('common.delete') }}
                      </v-tooltip>
                    </template>
                  </DeleteEmbedLinkDialog>
                </div>
              </div>
              <div v-else>
                <v-btn
                  v-on:click.prevent="createLink('origin')"
                  v-bind:disabled="isSubmitOriginDisabled"
                  elevation=0
                  color="primary"
                  class="mt-2"
                  block
                  data-cy="podMenu-tools-embedMediaOrigin-createLink-btn"
                >
                  {{ $t('routes.pod.menuEmbedMedia.createLink') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useEmbedMediaStore } from '@/stores/EmbedMediaStore.js'
import DeleteEmbedLinkDialog from "./DeleteEmbedLinkDialog.vue"
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { formatSize, videoDuration } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "EmbedMediaDialog",
  components: {
    DeleteEmbedLinkDialog,
  },
  props: {
    pod: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSubmitPreviewDisabled: false,
      isSubmitOriginDisabled: false,
      itemsLoaded: false,
      errorMessage: "",
      successMessage: "",
    };
  },
  mounted: function() {
    if (!this.pod) {
      return;
    }

    const embedMediaStore = useEmbedMediaStore();
    embedMediaStore.refreshEmbedLinks(this.pod.id)
        .then(() => {
          this.itemsLoaded = true;
        });
  },
  methods: {
    videoDuration,
    formatSize,
    createLink: function(type) {
      if (type === 'web') {
        this.isSubmitPreviewDisabled = true;
      } else {
        this.isSubmitOriginDisabled = true;
      }

      let self = this;

      const data = {
        podid: this.pod.id,
        asset: type,
      };

      const embedMediaStore = useEmbedMediaStore();
      embedMediaStore.createEmbedLink(data)
        .then(function() {
          if (type === 'web') {
            self.isSubmitPreviewDisabled = false;
          } else {
            self.isSubmitOriginDisabled = false;
          }

          return embedMediaStore.refreshEmbedLinks(self.pod.id);
        })
        .catch(function(error) {
          if (error && error.response && error.response.data && error.response.data.error) {
            self.commentTextError = error.response.data.error.message;
          } else {
            self.commentTextError = "Error!";
            console.log(error);   // unhandled error
          }
        });
    },
    copyToClipboard: function(type) {
      if (navigator && navigator.clipboard) {
        if (type === 'web') {
          navigator.clipboard.writeText(this.previewEmbedCode)
        } else {
          navigator.clipboard.writeText(this.originEmbedCode)
        }
      }
    },
  },
  computed: {
    embedLinks: function() {
      const embedMediaStore = useEmbedMediaStore();
      return embedMediaStore.getEmbedLinks || [];
    },
    previewLink: function() {
      const links = this.embedLinks;
      for (const link of links) {
        if (link.params.asset === 'web') {
          return link;
        }
      }

      return null;
    },
    originLink: function() {
      const links = this.embedLinks;
      for (const link of links) {
        if (link.params.asset === 'origin') {
          return link;
        }
      }

      return null;
    },
    rawAsset: function() {
      if (this.pod && this.pod.asset && this.pod.asset.raw && this.pod.asset.raw[0]) {
        return this.pod.asset.raw[0];
      }
      return {};
    },
    transcodedAsset: function() {
      if (this.isImagePod) {
        if (this.pod && this.pod.asset && this.pod.asset.thumbnail && this.pod.asset.thumbnail[0]) {
          return this.pod.asset.thumbnail[0];
        }
      } else {
        if (this.pod && this.pod.asset && this.pod.asset.web && this.pod.asset.web[0]) {
          return this.pod.asset.web[0];
        }
      }

      return {};
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    isImagePod: function() {
      const podStore = usePodStore();
      return podStore.isImagePod;
    },

    previewEmbedCode: function() {
      const cleanLink = this.previewLink.url.replace(/(^\w+:|^)\/\//, '');
      return '<iframe width="540" height="230" src="https://'+cleanLink+'" title="Tubeway" style="border: 0;" allowfullscreen></iframe>';
    },

    originEmbedCode: function() {
      const cleanLink = this.originLink.url.replace(/(^\w+:|^)\/\//, '');
      return '<iframe width="540" height="230" src="https://'+cleanLink+'" title="Tubeway" style="border: 0;" allowfullscreen></iframe>';
    }
  }

}
</script>

<style lang="scss" scoped>
.embed-media-dialog {
  &--asset-props {
    &-list {
      display: flex;
      flex-direction: column;
    }

    &-item {
      display: inline-flex;
      flex-direction: row;
      justify-content: space-between;
      max-width: 35%;

      &-name {
        font-weight: 700;
        &::after {
          content: ':';
        }
      }

      &-value {
        width: 40%;
      }
    }
  }
}
</style>
